/**
 * База движков: всё, что нужно любому движку и всем, кто на нём строит, — в одном месте. Блоки (`blocks/`) знают только
 * этот модуль и рисуют, анимируют и озвучивают через него; движки (`html.ts`, `pixi.ts`, `phaser.ts`, `two.ts`)
 * наследуют `Engine` и реализуют `Stage` и звук. Об игре здесь ничего нет.
 *
 *  - события и показы: `Emitter`, `ShowQueue`, `Run`;
 *  - звук: `Sound`, банк `SoundBank`, звук на Howler;
 *  - `Stage`: дерево узлов, текстуры и дорожки — всё, что блоки знают о рисовании;
 *  - кривые движения числами;
 *  - рецепты рисунков, общие для блоков;
 *  - `Engine`: хост, кадр, размер, плотность, отказ; логгер `LogEngine` (бывш. `null`) для проверок без браузера.
 */

// === события и показы ========================================================

/** Прямоугольник в CSS-пикселях относительно хоста. */
export interface Box {
  x: number
  y: number
  w: number
  h: number
}

export type Point = { x: number; y: number }

export interface Run {
  /** `true` — показ доигран, `false` — отменён. */
  readonly done: Promise<boolean>
  cancel(): void
}

/**
 * Показ, которым управляет тот, кто его играет. `finish` и `cancel` срабатывают один
 * раз: что пришло первым, то и решило исход, остальное молча игнорируется.
 */
export interface LiveRun extends Run {
  readonly settled: boolean
  finish(): void
}

function createRun(onCancel: () => void = () => {}): LiveRun {
  let settle: (done: boolean) => void = () => {}
  const done = new Promise<boolean>((resolve) => (settle = resolve))
  let settled = false
  const end = (value: boolean) => {
    if (settled) return
    settled = true
    settle(value)
  }
  return {
    done,
    get settled() {
      return settled
    },
    finish: () => end(true),
    cancel: () => {
      if (settled) return
      end(false)
      onCancel()
    },
  }
}

type Listener = (...args: never[]) => void

export class Emitter<E extends { [K in keyof E]: (...args: never[]) => void }> {
  private readonly listeners = new Map<keyof E, Set<Listener>>()

  on<K extends keyof E>(event: K, listener: E[K]): () => void {
    let set = this.listeners.get(event)
    if (!set) this.listeners.set(event, (set = new Set()))
    set.add(listener)
    return () => set.delete(listener)
  }

  emit<K extends keyof E>(event: K, ...args: Parameters<E[K]>): void {
    for (const listener of this.listeners.get(event) ?? [])
      (listener as unknown as (...a: Parameters<E[K]>) => void)(...args)
  }

  clear(): void {
    this.listeners.clear()
  }
}

/**
 * Очередь показов: один ждущий и один идущий. Новый показ отменяет оба; ждущий стартует,
 * когда сцена готова, идущий снимается своим `stop`. Одна на всех: правило «новый
 * отменяет прежний, до раскладки показ ждёт» не должно расходиться между движками.
 */
export class ShowQueue<T> {
  private queued: { what: T; run: LiveRun } | null = null
  private playing: { run: LiveRun; stop: () => void } | null = null

  /** Принять показ вместо прежних. `onCancel` — отмена именно этого показа, после его снятия. */
  push(what: T, onCancel: () => void = () => {}): LiveRun {
    this.cancel()
    const run: LiveRun = createRun(() => {
      this.end(run)
      onCancel()
    })
    this.queued = { what, run }
    return run
  }

  /** Ждущий показ, не забирая: сцена решает, готова ли она его играть. */
  get waiting(): { what: T; run: LiveRun } | null {
    return this.queued
  }

  /** Снять ждущий показ с очереди: сцена его стартует (или закрывает) сама. */
  dequeue(): void {
    this.queued = null
  }

  /** Показ пошёл: `stop` снимет его дорожки и таймеры при отмене и при конце. */
  begin(run: LiveRun, stop: () => void): void {
    this.playing = { run, stop }
  }

  /** Отменить и ждущий, и идущий. */
  cancel(): void {
    this.queued?.run.cancel()
    this.playing?.run.cancel()
  }

  /** Показ кончился сам: снять дорожки и отпустить место. `run.finish()` зовёт сцена. */
  end(run: LiveRun): void {
    if (this.queued?.run === run) this.queued = null
    if (this.playing?.run === run) {
      this.playing.stop()
      this.playing = null
    }
  }
}

// === звук ====================================================================

/**
 * Банк звуков: что играет каждая метка. Его отдаёт приложение, а не движок: движок знает только имена и файлы.
 * Формат файлов — `wav` (заглушки, собранные кодом) или `mp3` (настоящие звуки блоков).
 */
export interface SoundBank {
  readonly names: readonly string[]
  readonly format: 'wav' | 'mp3'
  /** Файл звука метки. */
  file(name: string): Uint8Array
  /** Метки, у которых тон каждый раз чуть другой: сто шагов подряд не должны звучать одним щелчком. */
  varied(name: string): boolean
}

export const mimeOf = (bank: SoundBank): string => (bank.format === 'mp3' ? 'audio/mpeg' : 'audio/wav')

export interface Sound {
  /** Снять блокировку автозвука. Зовётся из обработчика жеста. */
  unlock(): void
  /** Звук интерфейса: вопрос, ответ, победа. Звуки хода сцена ставит сама. */
  cue(name: string): void
  set(options: { muted?: boolean; volume?: number }): void
  /** Подключить банк: звуки готовятся заранее, первый не ждёт декодера. */
  load(bank: SoundBank): Promise<void>
}

/** Звук движка: метки интерфейса плюс звуки показа, которые отмена показа останавливает. */
export interface ShowSound extends Sound {
  play(name: string, owner?: object): void
  stop(owner: object): void
  destroy(): void
}

/*
 * Настройки живут на модуле, а не на экземпляре: после отказа движка партия уходит на другой,
 * и звук, выключенный игроком, не должен включиться. Один набор на все движки.
 */
export const soundSettings = { muted: false, volume: 1 }

/**
 * Звук без звука. Стоит, пока у движка нет звукового модуля: события идут по своим местам, но ничего не играют.
 */
class SilentSound implements ShowSound {
  unlock(): void {}
  cue(_name: string): void {}
  play(_name: string, _owner?: object): void {}
  stop(_owner: object): void {}
  set(_options: { muted?: boolean; volume?: number }): void {}
  load(_bank: SoundBank): Promise<void> {
    return Promise.resolve()
  }
  destroy(): void {}
}

/**
 * Звук на Howler: для `pixi` и `two`. Библиотека грузится отдельным чанком при подключении банка и только тем,
 * кто этот звук выбрал.
 *
 * Правила (README, «Паритет движков: звук»):
 *  - звуки показа принадлежат показу: отмена останавливает его играющие звуки, а звуки
 *    интерфейса и другого показа не трогает;
 *  - громкость и mute применяются до воспроизведения и переживают смену движка;
 *  - запрет автозвука и ошибка декодирования дают тишину, а не отказ сцены.
 */
export class HowlerSound implements ShowSound {
  private lib: typeof import('howler') | null = null
  private bank: SoundBank | null = null
  private readonly howls = new Map<string, import('howler').Howl>()
  private readonly urls: string[] = []
  /** Играющие звуки по владельцам (показам). */
  private readonly owned = new Map<object, Set<{ name: string; id: number }>>()

  async load(bank: SoundBank): Promise<void> {
    this.bank = bank
    this.lib ??= await import('howler')
    this.apply()
  }

  unlock(): void {
    const ctx = this.lib?.Howler.ctx as AudioContext | null | undefined
    if (ctx && ctx.state !== 'running') ctx.resume().catch(() => {})
  }

  cue(name: string): void {
    this.play(name)
  }

  set(options: { muted?: boolean; volume?: number }): void {
    if (options.muted !== undefined) soundSettings.muted = options.muted
    if (options.volume !== undefined) soundSettings.volume = Math.min(1, Math.max(0, options.volume))
    this.apply()
  }

  /** Сыграть метку; со `owner` — привязать звук к показу, чтобы отмена его остановила. */
  play(name: string, owner?: object): void {
    if (soundSettings.muted || !this.bank) return
    try {
      const howl = this.howl(name)
      const id = howl.play()
      // Небольшой разброс тона: сто шагов подряд не должны звучать как один и тот же щелчок.
      if (this.bank.varied(name)) howl.rate(0.94 + Math.random() * 0.12, id)
      if (owner) {
        const entry = { name, id }
        let set = this.owned.get(owner)
        if (!set) this.owned.set(owner, (set = new Set()))
        set.add(entry)
        howl.once('end', () => set.delete(entry), id)
      }
    } catch (error) {
      // Запрет автозвука или сбой декодирования — тишина, а не отказ сцены.
      console.warn(`[sound] ${name}: ${error instanceof Error ? error.message : String(error)}`)
    }
  }

  /** Остановить звуки показа и забыть его. */
  stop(owner: object): void {
    const set = this.owned.get(owner)
    this.owned.delete(owner)
    for (const { name, id } of set ?? []) this.howls.get(name)?.stop(id)
  }

  /** Сколько звуков показа ещё играет — для проверок. */
  playing(owner: object): number {
    return this.owned.get(owner)?.size ?? 0
  }

  destroy(): void {
    for (const howl of this.howls.values()) howl.unload()
    this.howls.clear()
    this.owned.clear()
    for (const url of this.urls) URL.revokeObjectURL(url)
    this.urls.length = 0
  }

  private howl(name: string) {
    let howl = this.howls.get(name)
    if (!howl) {
      const bank = this.bank!
      const url = URL.createObjectURL(new Blob([bank.file(name) as BlobPart], { type: mimeOf(bank) }))
      this.urls.push(url)
      howl = new this.lib!.Howl({ src: [url], format: [bank.format], preload: true })
      this.howls.set(name, howl)
    }
    return howl
  }

  private apply(): void {
    this.lib?.Howler.mute(soundSettings.muted)
    this.lib?.Howler.volume(soundSettings.volume)
  }
}

// === Stage: что блоки знают о рисовании ====================================
/*
 * Сцена, как её видят компоненты (`dice.ts`, `board.ts`, `pawn.ts`, `space.ts`): дерево узлов, на
 * листьях — картинки из текстур. Ничего про движок здесь нет: узлы и текстуры создаёт `Stage`, а его реализует тот,
 * кто рисует (`pixi.ts`, `phaser.ts`). Компонент пишется один раз и идёт на любом движке с холстом.
 *
 * Единицы — родительские: у узла `x`, `y` и размер лежат в единицах родителя, `scale` растягивает и самого узла.
 */

/** Контекст рисования: подмножество `CanvasRenderingContext2D`, что одинаково исполняют холст и запись в SVG (`html.ts`). */
export type Ctx = CanvasRenderingContext2D

/** Контур SVG, что знает свой текст: холст рисует его как `Path2D`, запись в SVG — как `<path d>`. */
export type Shape = Path2D & { readonly d: string }

export const shape = (d: string): Shape => Object.assign(new Path2D(d), { d })

/** Рисунок: размер в пикселях и то, как он рисуется. Во что он превратится, решает движок: в растр или в вектор. */
export interface Drawing {
  readonly width: number
  readonly height: number
  readonly draw: (c: Ctx) => void
}

export const drawing = (width: number, height: number, draw: (c: Ctx) => void): Drawing => ({
  width,
  height,
  draw,
})

/** Рисунок на холсте: для движков, что рисуют растром. */
export function rasterize({ width, height, draw }: Drawing): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  draw(canvas.getContext('2d')!)
  return canvas
}

/** Рисунок или картинка, загруженные в движок. */
export interface Tex {
  readonly width: number
  readonly height: number
  /** Живая текстура: рисунок заменяется и грузится заново (глаз на время моргания). */
  redraw(draw: (c: Ctx) => void): void
  /**
   * Текстура больше не нужна, но картинка ещё может её показывать до конца кадра: видеопамять освобождается позже, и
   * сама текстура может быть уже разобрана вместе с движком.
   */
  release(): void
}

/**
 * Основа текстуры, которой владеет движок и которую показывают картинки: видеопамять освобождается, когда текстуру
 * отпустил владелец (`release`) и её больше никто не показывает. Отпущенную, но показываемую текстуру сносить нельзя: картинка
 * загрузила бы её заново, и она осталась бы в видеопамяти навсегда (так копились тела фишек в Pixi при смене размера окна).
 */
export abstract class Leased implements Tex {
  abstract readonly width: number
  abstract readonly height: number
  abstract redraw(draw: (c: Ctx) => void): void
  private users = 0
  private released = false
  private freed = false

  /** Освободить видеопамять: текстура больше не нужна ни владельцу, ни тем, кто её показывал. */
  protected abstract free(): void

  /** Картинка начала или перестала показывать текстуру. */
  hold(by: 1 | -1): void {
    this.users += by
    this.settle()
  }

  release(): void {
    this.released = true
    this.settle()
  }

  /**
   * Снятая с экрана текстура уходит не сразу: движок мог ещё не дорисовать кадр с ней, а сама она (WebGPU держит текстуру
   * в группе привязок) от немедленного сноса даёт предупреждение.
   */
  private settle(): void {
    if (!this.released || this.users > 0 || this.freed) return
    window.setTimeout(() => {
      if (this.users > 0 || this.freed) return
      this.freed = true
      this.free()
    }, 2000)
  }
}

export type Source = Drawing | HTMLImageElement

/** Что дорожка меняет у узла; чего нет в позе — не трогает. */
export interface Pose {
  x?: number
  y?: number
  /** Радианы. */
  rotation?: number
  alpha?: number
  scale?: number
}

/**
 * Дорожка: позы узла по доле `at` длительности (от 0 до 1), между ними линейно. Одна запись на любой движок:
 * компоновщик браузера (`dom`, `svg`) играет её сам, холстам ту же дорожку считает `Tracker` на каждом кадре.
 */
export interface Track {
  /** Мс. */
  duration: number
  /** Петля или один проход; после прохода узел остаётся в последней позе. */
  loop: boolean
  frames: ReadonlyArray<Pose & { at: number }>
}

/** Играющая дорожка: пока не снята, узлом управляет она. */
export interface Playing {
  cancel(): void
}

export interface Item {
  x: number
  y: number
  /** Радианы. */
  rotation: number
  alpha: number
  visible: boolean
  /** Равномерный масштаб; у картинки он считается от размера её текстуры. */
  scale: number
  at(x: number, y: number): this
  add(...items: Item[]): this
  /** Убирает и уничтожает всех детей; их текстуры остаются у владельца. */
  clear(): void
  destroy(): void
  /**
   * Играет дорожку: её ноль — момент `start` на часах сцены (`Stage.epoch`). Нигде на пути от узла к корню нет другого
   * узла с дорожкой, а новая дорожка заменяет прежнюю.
   */
  play(track: Track, start: number): Playing
}

export interface Img extends Item {
  /** После смены текстуры размер задаётся заново (`fit`). */
  tex: Tex
  /** Размер на экране в единицах родителя; один аргумент — квадрат. */
  fit(width: number, height?: number): this
}

export interface Stage {
  /** Корень сцены: плотность экрана ставит тот, кто его создал. */
  readonly root: Item
  /** Нуль часов сцены — `performance.now()` монтирования: по ним идут дорожки, как и время неба. */
  epoch: number
  /** Кадр: холсты считают дорожки на этот момент (`performance.now()`), разметка играет их сама. */
  tick(now: number): void
  group(...items: Item[]): Item
  /** Картинка от центра (`center`) или от левого верхнего угла (`corner`); без текстуры — пустая. */
  img(tex?: Tex, anchor?: 'center' | 'corner'): Img
  /** `mip` — мип-цепочка: текстура сильно уменьшается, без неё картинка искрит. */
  texture(source: Source, mip?: boolean): Tex
}

/** Кэш по ключу, давние впереди: текстуру, которую пересоздают на тех же значениях, берут снова, а не рисуют. */
export class Cache<V> {
  private readonly map = new Map<string, V>()

  constructor(
    private readonly max: number,
    /** Лишнее сверх запаса уходит сюда. */
    private readonly drop: (value: V) => void,
  ) {}

  get(key: string, make: () => V): V {
    let value = this.map.get(key)
    if (value !== undefined) this.map.delete(key)
    else value = make()
    this.map.set(key, value)
    if (this.map.size > this.max) {
      const [stale] = this.map
      this.map.delete(stale[0])
      this.drop(stale[1])
    }
    return value
  }

  /** Движок закрывается: текстуры уйдут вместе с ним. */
  clear(): void {
    this.map.clear()
  }
}

/**
 * Плотность растра берётся из сетки с шагом в 6 %, вверх: размеры повторяются, а значит, текстуры можно хранить и брать
 * снова, а не пересоздавать на каждый пиксель смены размера окна.
 */
export const gridPx = (px: number): number => 1.0625 ** Math.ceil(Math.log(px) / Math.log(1.0625))

/** Чем больше уровень, тем резче растр: плотность округляется вверх до степени двойки, чтобы не пересобирать его на каждый пиксель окна. */
export const bucket = (px: number, max = 4): number =>
  Math.min(max, 2 ** Math.ceil(Math.log2(Math.max(px, 0.25))))

/** Поза дорожки через `ms` после её начала; петля заворачивается, проход держит последнюю. */
function poseAt(track: Track, ms: number): Pose {
  const { frames, duration } = track
  const t = track.loop ? ((ms % duration) + duration) % duration : Math.min(Math.max(ms, 0), duration)
  const at = t / duration
  let hi = frames.findIndex((f) => f.at >= at)
  if (hi < 0) hi = frames.length - 1
  const b = frames[hi]
  const a = frames[Math.max(0, hi - 1)]
  const k = b.at > a.at ? Math.min(1, Math.max(0, (at - a.at) / (b.at - a.at))) : 1
  const mix = (u?: number, v?: number) => (u === undefined || v === undefined ? (v ?? u) : u + (v - u) * k)
  return {
    x: mix(a.x, b.x),
    y: mix(a.y, b.y),
    rotation: mix(a.rotation, b.rotation),
    alpha: mix(a.alpha, b.alpha),
    scale: mix(a.scale, b.scale),
  }
}

/** Дорожка из функции времени: `pose(ms)` берётся через `step` мс на всей длительности и на её конце. */
export function sampled(pose: (ms: number) => Pose, duration: number, step: number, loop: boolean): Track {
  const n = Math.max(1, Math.ceil(duration / step))
  return {
    duration,
    loop,
    frames: Array.from({ length: n + 1 }, (_, i) => ({ at: i / n, ...pose((i / n) * duration) })),
  }
}

/** Дорожки для сцены, которая считает позы сама, на каждом кадре (`pixi`, `phaser`). */
export class Tracker {
  private readonly plays = new Map<Item, { track: Track; start: number }>()

  play(item: Item, track: Track, start: number): Playing {
    const entry = { track, start }
    this.plays.set(item, entry)
    return {
      cancel: () => {
        if (this.plays.get(item) === entry) this.plays.delete(item)
      },
    }
  }

  drop(item: Item): void {
    this.plays.delete(item)
  }

  /** `now` — мс на часах сцены. */
  tick(now: number): void {
    for (const [item, { track, start }] of this.plays) {
      if (now < start) continue
      const pose = poseAt(track, now - start)
      if (pose.x !== undefined || pose.y !== undefined) item.at(pose.x ?? item.x, pose.y ?? item.y)
      if (pose.rotation !== undefined) item.rotation = pose.rotation
      if (pose.alpha !== undefined) item.alpha = pose.alpha
      if (pose.scale !== undefined) item.scale = pose.scale
    }
  }
}

// === кривые движения =======================================================
/*
 * Кривые движения данными: функция времени для холста и ключи дорожек для разметки. Одна на все движки, иначе одна и
 * та же фишка в двух движках приземлялась бы по-разному.
 *
 * Здесь только кривые, которыми ходит фишка. Кривые декоративных петель — дело самих петель.
 */

export type EaseName = 'linear' | 'step' | 'drop'

type Curve = { kind: 'bezier'; points: [number, number, number, number] } | { kind: 'stops'; stops: number[] }

/*
 * Шаг по клетке (`step`): резкий старт и мягкая посадка, без перелёта — на сетке промах мимо клетки читается как
 * ошибка, а не как живость. Падение в колодец (`drop`): доля пути растёт как квадрат времени — разгон под тяжестью, —
 * а после удара о дно идут параболические отскоки: первый на 7 % пролёта, каждый следующий втрое ниже.
 */
const EASES: Record<Exclude<EaseName, 'linear'>, Curve> = {
  step: { kind: 'bezier', points: [0.34, 0.9, 0.3, 1] },
  drop: {
    kind: 'stops',
    stops: [
      0, 0.0013, 0.005, 0.0113, 0.0202, 0.0315, 0.0454, 0.0618, 0.0807, 0.1021, 0.1261, 0.1526, 0.1816,
      0.2131, 0.2472, 0.2837, 0.3228, 0.3644, 0.4086, 0.4552, 0.5044, 0.5561, 0.6104, 0.6671, 0.7264, 0.7882,
      0.8525, 0.9193, 0.9887, 0.9756, 0.9534, 0.9385, 0.931, 0.9309, 0.9381, 0.9528, 0.9748, 0.9978, 0.9848,
      0.9792, 0.981, 0.9902, 0.997, 0.9937, 1,
    ],
  },
}

/**
 * Значение кривой в момент `t` от 0 до 1 — то, что Web Animations делает с той
 * же записью: `cubic-bezier` решается по x, `linear()` с равными шагами
 * интерполируется между соседними точками.
 */
export function ease(name: EaseName, t: number): number {
  const at = Math.min(1, Math.max(0, t))
  if (name === 'linear') return at
  const curve = EASES[name]
  return curve.kind === 'bezier' ? bezier(curve.points, at) : stops(curve.stops, at)
}

function stops(values: number[], t: number): number {
  const last = values.length - 1
  const i = Math.min(last - 1, Math.floor(t * last))
  const f = t * last - i
  return values[i] + (values[i + 1] - values[i]) * f
}

/*
 * `cubic-bezier(x1, y1, x2, y2)` — кривая от (0, 0) до (1, 1). Время — это x, и
 * параметр кривой для него ищется численно: Ньютон с запасным делением пополам,
 * как в самих браузерах. Точность — десятитысячная доли, глазу хватает с запасом.
 */
export function bezier([x1, y1, x2, y2]: [number, number, number, number], x: number): number {
  const cx = 3 * x1
  const bx = 3 * (x2 - x1) - cx
  const ax = 1 - cx - bx
  const cy = 3 * y1
  const by = 3 * (y2 - y1) - cy
  const ay = 1 - cy - by
  const curveX = (s: number) => ((ax * s + bx) * s + cx) * s
  const curveY = (s: number) => ((ay * s + by) * s + cy) * s
  const slopeX = (s: number) => (3 * ax * s + 2 * bx) * s + cx

  let s = x
  for (let i = 0; i < 8; i++) {
    const error = curveX(s) - x
    if (Math.abs(error) < 1e-6) return curveY(s)
    const slope = slopeX(s)
    if (Math.abs(slope) < 1e-6) break
    s -= error / slope
  }
  let lo = 0
  let hi = 1
  s = x
  while (hi - lo > 1e-6) {
    if (curveX(s) < x) lo = s
    else hi = s
    s = (lo + hi) / 2
  }
  return curveY(s)
}

/**
 * Момент касания внутри отрезка, долей его длительности.
 *
 * Падение в колодец кончается отскоками: кривая `drop` доходит до дна, уходит
 * обратно вверх и успокаивается только к концу отрезка. Клетка загорается от
 * удара, а не от того, что тело перестало прыгать, — значит нужен первый
 * разворот кривой, а не её конец. У кривых без отскока разворота нет, доля
 * выходит единицей.
 */
export function contactAt(name: EaseName): number {
  if (name === 'linear') return 1
  const curve = EASES[name]
  if (curve.kind !== 'stops') return 1
  const values = curve.stops
  const turn = values.findIndex((v, i) => i + 1 < values.length && values[i + 1] < v)
  return turn === -1 ? 1 : turn / (values.length - 1)
}

// === рецепты рисунков ======================================================
// Общие для блоков: цвета палитры, холст, символы SVG-листов, тень и рамка.

/** Картинка по адресу, готовая к показу. */
export async function loadImage(url: string): Promise<HTMLImageElement> {
  const img = new Image()
  img.src = url
  await img.decode()
  return img
}

/**
 * Что приложение даёт сцене: блоки называют лист, картинку или цвет по имени, а откуда они берутся (HTTP, переменные
 * палитры `index.main.css`, R106), знает приложение (`app.scene.ts`).
 */
export interface Resources {
  /** Текст SVG-листа по имени файла. */
  sheet(file: string): Promise<string>
  /** Картинка по имени файла, уже декодированная. */
  image(file: string): Promise<HTMLImageElement>
  /** Цвет палитры по имени (`--color-text`). */
  color(name: string): string
}

export const rgba = (hex: string, alpha: number): string => {
  const n = parseInt(hex.slice(1), 16)
  return `rgba(${n >> 16},${(n >> 8) & 255},${n & 255},${alpha})`
}

/**
 * Картинки на 2D-холсте рисуются с лучшим сглаживанием. Настройка живёт в контексте и сбрасывается, когда меняют
 * размер холста, поэтому зовут после каждого `width`/`height`. У холста с WebGL контекста 2D нет — тогда ничего.
 */
export function smoothed(canvas: HTMLCanvasElement): void {
  const c = canvas.getContext('2d')
  if (!c) return
  c.imageSmoothingEnabled = true
  c.imageSmoothingQuality = 'high'
}

/** Радиальная заливка от центра к краю: `stops` — доля радиуса и цвет; `round` — круг, а не квадрат. */
export function radialDrawing(size: number, stops: Array<[number, string]>, round = false): Drawing {
  return drawing(size, size, (c) => {
    const g = c.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2)
    for (const [at, color] of stops) g.addColorStop(at, color)
    c.fillStyle = g
    if (round) {
      c.beginPath()
      c.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2)
      c.fill()
    } else c.fillRect(0, 0, size, size)
  })
}

/** Свет на сцене один, и тень у всего падает одинаково: плотное ядро, быстрый спад, мягкий край. */
export const shadowDrawing = (): Drawing =>
  radialDrawing(128, [
    [0, 'rgba(0,0,0,.75)'],
    [0.45, 'rgba(0,0,0,.4)'],
    [0.75, 'rgba(0,0,0,.12)'],
    [1, 'rgba(0,0,0,0)'],
  ])

/** Символ SVG-листа, разобранный браузером: рисуется на любом размере, векторы пересчитываются. */
export interface SymbolImage {
  img: HTMLImageElement
  /** Размер `viewBox` символа. */
  w: number
  h: number
}

/**
 * Символ SVG-листа как картинка: один исходник (`src/public/`) для всех реализаций. `scale` — пикселей
 * на единицу `viewBox` в самой картинке (Safari рисует SVG в этом размере, остальные — в размере вызова).
 */
export async function symbolImage(sheet: string, id: string, scale: number): Promise<SymbolImage> {
  const doc = new DOMParser().parseFromString(sheet, 'image/svg+xml')
  const symbol = doc.getElementById(id)
  if (!symbol) throw new Error(`в листе нет символа ${id}`)
  const [, , w, h] = symbol.getAttribute('viewBox')!.split(/\s+/).map(Number)
  const defs = doc.querySelector('defs')!.cloneNode(true) as Element
  defs.querySelectorAll('symbol').forEach((s) => s.remove())
  const xml = new XMLSerializer()
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${symbol.getAttribute('viewBox')}" ` +
    `width="${w * scale}" height="${h * scale}">${xml.serializeToString(defs)}` +
    `${[...symbol.childNodes].map((n) => xml.serializeToString(n)).join('')}</svg>`
  // Адрес остаётся жить вместе с картинкой: на неё ссылается и запись в SVG (`drawImage` в `html.ts`).
  const img = await loadImage(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`)
  return { img, w, h }
}

/**
 * Рамка — скруглённый прямоугольник `w`×`h` одной линией `line` цвета `color` — одним холстом: вспышка касания клетки
 * и кольцо кубика. Размеры в единицах кадра, `px` — пикселей холста на единицу; центр рамки — центр холста, с запасом
 * на половину линии и сглаживание, так что картинка ставится по центру объекта. Рисуется один раз на размер и плотность.
 */
export const frameDrawing = (
  w: number,
  h: number,
  radius: number,
  line: number,
  color: string,
  px: number,
): Drawing =>
  drawing(Math.ceil((w + line) * px) + 4, Math.ceil((h + line) * px) + 4, (c) => {
    c.translate(c.canvas.width / 2, c.canvas.height / 2)
    c.scale(px, px)
    c.lineWidth = line
    c.strokeStyle = color
    c.beginPath()
    c.roundRect(-w / 2, -h / 2, w, h, radius)
    c.stroke()
  })

// === движок ==================================================================

export type EngineKind = 'html' | 'pixi' | 'phaser' | 'two' | 'null'

/** Указатель над хостом. `x` и `y` — в пикселях хоста; `free()` — над точкой нет другого интерфейса (спрашивают только тогда, когда без этого не обойтись: проверка не бесплатна). */
export interface EnginePointer {
  phase: 'down' | 'move' | 'up' | 'cancel'
  x: number
  y: number
  free: () => boolean
}

interface EngineEvents {
  /** Холст подогнан под новый размер хоста или новую плотность пикселей (`density`): слои сцены пора поставить заново. */
  resize: (density: boolean) => void
  /** Движок не может работать дальше; приложение переходит на движок по умолчанию. */
  error: (error: Error) => void
  pointer: (e: EnginePointer) => void
}

/** Не чаще одной пересборки слоёв за столько, пока размер окна тянут. */
const RESIZE_MS = 100

/** Плотность пикселей экрана; без окна (проверки в node) — единица. */
export const density = (): number => globalThis.devicePixelRatio || 1

/**
 * База любого движка: хост, цикл кадров, размер и плотность, звук, отказ. Движок об игре ничего не знает: он даёт
 * тому, кто строит сцену, `Stage` (что и как нарисовано — дело строящего), часы и такт кадра. Наследник открывает
 * холст (`open`), подгоняет его (`fit`), просит кадр (`invalidate`) и рисует его после `step`.
 */
export abstract class Engine {
  abstract readonly kind: EngineKind
  abstract readonly sound: ShowSound
  readonly events = new Emitter<EngineEvents>()
  host: HTMLElement | null = null
  /** Сцена движка: есть с монтирования. */
  stage: Stage | null = null
  alive = false
  ready = false
  /** Сцена спрятана (лобби): собрана, но кадров не рисует. */
  parked = false
  size = { width: 0, height: 0 }
  /** Когда смонтировались: по этим часам идут дорожки и небо. */
  mountedAt = 0
  /** Кадр сцены: зовётся на каждом такте до отрисовки; `dt` — сколько прошло на самом деле. */
  onFrame: ((now: number, dt: number) => void) | null = null
  /** Во сколько раз показы идут быстрее: множитель сервера, его ставит приложение (`setTimeScale`). */
  speed = 1
  private own: HTMLElement | null = null
  private ro: ResizeObserver | null = null
  private offInput: Array<() => void> = []
  private pointing = false
  private offDensity: (() => void) | null = null
  /** Когда размер пересобирался в последний раз и ждёт ли ещё один пересбор в конце серии. */
  private resizedAt = 0
  private resizeTimer = 0
  private last = 0

  // --- что даёт наследник ---

  /** Холст и `Stage` (`this.stage`) в `own`. `false` — демонтировали на ходу, наследник уже прибрался за собой. */
  protected abstract open(host: HTMLElement, own: HTMLElement): Promise<boolean>
  /** Отпустить холст и всё, что держит наследник. */
  protected abstract close(): void
  /** Размер холста под хост. */
  protected abstract fit(width: number, height: number): void
  /** Попросить кадр: наследник ставит его в очередь у браузера; такт идёт на каждом кадре и сам не засыпает, пока сцена не спрятана (`parked`). */
  abstract invalidate(): void
  /** Остановить (`true`) или запустить цикл кадров, который ведёт не `invalidate`, а сама библиотека. */
  protected sleep(_on: boolean): void {}

  // --- что подменяют проверки без браузера ---

  now(): number {
    return performance.now()
  }

  protected measure(): { width: number; height: number } {
    return { width: this.host?.clientWidth ?? 0, height: this.host?.clientHeight ?? 0 }
  }

  protected layer(host: HTMLElement): HTMLElement {
    const own = document.createElement('div')
    own.className = 'absolute inset-0'
    host.append(own)
    this.own = own
    return own
  }

  /** Следим за размером хоста, плотностью пикселей и указателем. */
  protected watch(host: HTMLElement): void {
    this.ro = new ResizeObserver(() => this.resizeSoon())
    this.ro.observe(host)
    this.watchDensity()
    const on = (
      type: 'pointerdown' | 'pointermove' | 'pointerup' | 'pointercancel',
      phase: 'down' | 'move' | 'up' | 'cancel',
    ) => {
      const fn = (e: PointerEvent) => {
        const origin = host.getBoundingClientRect()
        this.events.emit('pointer', {
          phase,
          x: e.clientX - origin.left,
          y: e.clientY - origin.top,
          // Поверх сцены не должно быть другого интерфейса: наверху только предки хоста.
          free: () => {
            const top = document.elementFromPoint(e.clientX, e.clientY)
            return !top || top === document.body || top.contains(host)
          },
        })
      }
      document.addEventListener(type, fn, true)
      this.offInput.push(() => document.removeEventListener(type, fn, true))
    }
    on('pointerdown', 'down')
    on('pointermove', 'move')
    on('pointerup', 'up')
    on('pointercancel', 'cancel')
  }

  /** Курсор-указатель над сценой: нужен тому, кто знает, что под указателем можно нажать. */
  cursor(on: boolean): void {
    if (on === this.pointing || typeof document === 'undefined') return
    this.pointing = on
    document.body.style.cursor = on ? 'pointer' : ''
  }

  // --- жизненный цикл ---

  async mount(host: HTMLElement): Promise<void> {
    this.alive = true
    this.mountedAt = this.now()
    this.host = host
    if (!(await this.open(host, this.layer(host)))) return
    this.watch(host)
    this.ready = true
    this.resize(false)
  }

  park(on: boolean): void {
    if (this.parked === on) return
    this.parked = on
    this.sleep(on)
    if (on || !this.ready) return
    // Часы кадров начинаются заново, размер хоста мог смениться, пока сцена спала.
    this.last = 0
    this.resize(false)
  }

  destroy(): void {
    this.alive = false
    this.ready = false
    this.events.clear()
    this.onFrame = null
    this.ro?.disconnect()
    for (const off of this.offInput) off()
    this.offInput = []
    this.cursor(false)
    if (this.resizeTimer) window.clearTimeout(this.resizeTimer)
    this.resizeTimer = 0
    this.offDensity?.()
    this.sound.destroy()
    this.close()
    this.own?.remove()
    this.own = null
    this.host = null
  }

  /** Сбой отрисовки (потеря контекста, отказ драйвера) не роняет страницу: приложение уходит на движок по умолчанию. */
  guard(fn: () => void): void {
    try {
      fn()
    } catch (error) {
      this.alive = false
      this.events.emit('error', error instanceof Error ? error : new Error(String(error)))
    }
  }

  // --- такт ---

  /**
   * Один кадр состояния: дорожки сцены на этот момент и кадр того, кто на ней строит. Время идёт по часам: `dt` — сколько
   * прошло на самом деле, и затянувшийся кадр не замедляет ни небо, ни свет, ни кольцо кубика.
   */
  protected step(): void {
    const now = this.now()
    const dt = this.last ? now - this.last : 0
    this.last = now
    this.stage?.tick(now)
    this.onFrame?.(now, dt)
  }

  // --- размер и плотность ---

  /**
   * Плотность пикселей меняется и при том же размере хоста: окно перенесли на другой экран, масштаб страницы сменили.
   * Размер хоста при этом тот же, `ResizeObserver` молчит, а холст и текстуры остаются под прежнюю плотность и мылят.
   * Следим за `resolution` медиазапроса: на уходе плотности с прежнего значения холст, поле и фишки собираются заново.
   */
  private watchDensity(): void {
    const query = matchMedia(`(resolution: ${density()}dppx)`)
    const changed = () => {
      this.guard(() => this.resize(true))
      this.watchDensity()
    }
    query.addEventListener('change', changed, { once: true })
    this.offDensity = () => query.removeEventListener('change', changed)
  }

  /**
   * Размер хоста при перетаскивании края окна меняется на каждом кадре, а пересборка слоёв (холсты, поле, фишки) стоит
   * кадра и больше. Первая смена пересобирается сразу, дальше не чаще раза в `RESIZE_MS`, и последняя — в конце серии:
   * между пересборками слои держат прежний размер, а итог всегда по последнему размеру хоста.
   */
  private resizeSoon(): void {
    if (this.resizeTimer) return
    const wait = this.resizedAt + RESIZE_MS - performance.now()
    const run = () => {
      this.resizeTimer = 0
      this.resizedAt = performance.now()
      this.guard(() => this.resize(false))
    }
    if (wait <= 0) run()
    else this.resizeTimer = window.setTimeout(run, wait)
  }

  private resize(densityChanged: boolean): void {
    if (!this.host || !this.ready) return
    const { width, height } = this.measure()
    if (width === 0 || height === 0) return
    this.size = { width, height }
    this.fit(width, height)
    this.events.emit('resize', densityChanged)
  }
}

/**
 * Движок, которого ещё нет. Отказывает на монтировании, и приложение переходит на движок по умолчанию — тем же
 * путём, каким уйдёт с настоящего при потере WebGL-контекста. Так путь отказа работает и проверяется уже сейчас.
 */
export class Placeholder extends Engine {
  readonly sound = new SilentSound()

  constructor(readonly kind: EngineKind) {
    super()
  }

  protected open(): Promise<boolean> {
    return Promise.reject(new Error(`движок «${this.kind}» ещё не подключён`))
  }

  protected close(): void {}
  protected fit(): void {}
  invalidate(): void {}
}

// === логгер ==================================================================

/** Отложить вызов; возвращает отмену. */
type Later = (fn: () => void, ms: number) => () => void

/** Часы движка-логгера: проверки подставляют свои и проматывают время, а не ждут его. */
export interface LogClock {
  now(): number
  later: Later
}

const realClock: LogClock = {
  now: () => performance.now(),
  later: (fn, ms) => {
    const id = setTimeout(fn, ms)
    return () => clearTimeout(id)
  },
}

class LogItem implements Item {
  x = 0
  y = 0
  rotation = 0
  alpha = 1
  visible = true
  scale = 1

  at(x: number, y: number): this {
    this.x = x
    this.y = y
    return this
  }

  add(): this {
    return this
  }

  clear(): void {}
  destroy(): void {}

  play(): Playing {
    return { cancel() {} }
  }
}

const logTex = (width: number, height: number): Tex => ({ width, height, redraw() {}, release() {} })

class LogImg extends LogItem implements Img {
  tex: Tex = logTex(1, 1)

  fit(): this {
    return this
  }
}

/** Сцена, которая ничего не рисует: узлы создаются и принимают вызовы, картинки нет. */
class LogStage implements Stage {
  readonly root = new LogItem()
  epoch = 0

  tick(): void {}

  group(): Item {
    return new LogItem()
  }

  img(tex?: Tex): Img {
    const img = new LogImg()
    if (tex) img.tex = tex
    return img
  }

  texture(source: Source): Tex {
    return logTex(source.width, source.height)
  }
}

class LogSound extends SilentSound {
  /** Какие звуки сыграны, по порядку. */
  readonly played: string[] = []

  override cue(name: string): void {
    this.played.push(name)
  }

  override play(name: string): void {
    this.played.push(name)
  }
}

interface LogOptions {
  /** Размер воображаемого хоста. */
  size?: { width: number; height: number }
  clock?: LogClock
  /** Через сколько мс идёт кадр: проверкам с проматываемыми часами нужен кадр в миллисекунду. */
  frameMs?: number
}

/**
 * Движок `null`: ничего не рисует, но ведёт себя по контракту — часы, кадры, размер, звук. Нужен проверкам: на нём
 * в `npm run verify` сцена из блоков идёт без браузера и без сети. Годится и в живом приложении (`?engine=null`):
 * партия идёт, сцены нет.
 */
export class LogEngine extends Engine {
  readonly kind = 'null' as const
  readonly sound = new LogSound()
  private readonly clock: LogClock
  private readonly frameMs: number
  private readonly hostSize: { width: number; height: number }
  private stop: (() => void) | null = null

  constructor({ size = { width: 1280, height: 720 }, clock = realClock, frameMs = 16 }: LogOptions = {}) {
    super()
    this.clock = clock
    this.frameMs = frameMs
    this.hostSize = size
  }

  override now(): number {
    return this.clock.now()
  }

  protected override measure(): { width: number; height: number } {
    return this.hostSize
  }

  protected override layer(host: HTMLElement): HTMLElement {
    return host
  }

  protected override watch(): void {}

  protected open(): Promise<boolean> {
    this.stage = new LogStage()
    return Promise.resolve(true)
  }

  protected close(): void {
    this.stop?.()
    this.stop = null
    this.stage = null
  }

  protected fit(): void {}

  /** Кадр идёт через `frameMs` и просит следующий: как у настоящих движков, такт не засыпает. */
  invalidate(): void {
    if (this.stop || !this.alive || this.parked) return
    this.stop = this.clock.later(() => {
      this.stop = null
      if (!this.ready) return
      this.guard(() => this.step())
      this.invalidate()
    }, this.frameMs)
  }
}
